import React, { useState } from "react";
import LanguageSwitcher from "./LanguageSwitcher";
import { useT } from "../i18n";
import { joinSharedProject } from "../api/joinProject";
import PickerCookieHint from "./PickerCookieHint";

interface ProjectChooserProps {
  onCreate: () => void;
  onJoined: () => void;
}

// First screen after sign-in when no project is set up yet: choose between creating a
// new project (the setup wizard) or joining a project a manager shared (fieldworker).
export default function ProjectChooser({ onCreate, onJoined }: ProjectChooserProps) {
  const t = useT();
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function handleJoin() {
    setError("");
    setNotice("");
    setJoining(true);
    try {
      const res = await joinSharedProject();
      if (res.status === "joined") {
        onJoined();
      } else if (res.status === "owned") {
        setNotice(t("onboard.ownedBody", { name: res.name }));
      } else if (res.status === "error") {
        setError(res.message);
      }
      // "cancelled" — do nothing, stay on the chooser
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setJoining(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 via-white to-accent-50 p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center space-y-5 border border-gray-100">
        <div className="flex justify-end">
          <LanguageSwitcher />
        </div>
        <img
          src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`}
          srcSet={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png 1x, ${process.env.PUBLIC_URL || ""}/assets/logo_256x256.png 2x`}
          alt="MeW"
          className="mx-auto w-16 h-16"
        />
        <div className="space-y-1">
          <h1 className="text-xl font-bold text-pink-700">{t("onboard.chooseTitle")}</h1>
          <p className="text-sm text-gray-600">{t("onboard.chooseSubtitle")}</p>
        </div>

        <div className="space-y-3 text-left">
          <button
            type="button"
            onClick={onCreate}
            disabled={joining}
            className="w-full rounded-lg border border-gray-200 hover:border-pink-300 hover:bg-pink-50 p-4 transition-colors disabled:opacity-50"
          >
            <div className="flex items-center gap-3">
              <span className="text-pink-600 text-lg"><i className="fa-solid fa-folder-plus" aria-hidden="true" /></span>
              <div>
                <div className="font-medium text-gray-900">{t("onboard.createTitle")}</div>
                <div className="text-sm text-gray-500">{t("onboard.createDesc")}</div>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={handleJoin}
            disabled={joining}
            className="w-full rounded-lg border border-gray-200 hover:border-pink-300 hover:bg-pink-50 p-4 transition-colors disabled:opacity-50"
          >
            <div className="flex items-center gap-3">
              <span className="text-pink-600 text-lg">
                <i className={`fa-solid ${joining ? "fa-spinner fa-spin" : "fa-people-group"}`} aria-hidden="true" />
              </span>
              <div>
                <div className="font-medium text-gray-900">{t("onboard.joinTitle")}</div>
                <div className="text-sm text-gray-500">{joining ? t("onboard.joining") : t("onboard.joinDesc")}</div>
              </div>
            </div>
          </button>
        </div>

        <PickerCookieHint />
        {notice && <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">{notice}</div>}
        {error && <div className="text-sm text-red-600">{t("onboard.joinError", { msg: error })}</div>}
      </div>
    </div>
  );
}
